import axios from 'axios';
import { mnemosyneBaseUrl } from '../../utils/axios-interceptor';

// Mnemosyne-backed S3 storage for document bodies that don't live inline on the
// Mongo row. Extracted from SheetEditor/sheetStorage when PDFs needed the same
// versioned-key lifecycle; the caller supplies bytes, mime and extension.

// ---------------------------------------------------------------------------
// Mnemosyne category + flow (hardcoded constants — the app convention)
// ---------------------------------------------------------------------------

// App-internal editor blobs, invisible to Data Room and Magic Upload via null
// docType + no babler registration. 100649/16 exist in every environment.
export const EDITOR_BLOBS_CATEGORY_ID = 100649;
export const EDITOR_BLOBS_FLOW_ID = 16;

/**
 * `"<numericDocId>"` or `"<numericDocId>_<versionNumber>"` — all numeric.
 *
 * The two forms ARE the two storage modes. A versioned bucket answers a PUT
 * with `x-amz-version-id`, so every save is registered as a numbered version and
 * the ref carries that number. A bucket without versioning (or whose CORS does
 * not expose the header) has no version to name: the save simply overwrites the
 * one object, the ref stays bare, and reads resolve to whatever is current.
 */
export const STORAGE_DOC_ID = /^\d+(_\d+)?$/;

// aica's write-boundary guard requires this exact literal and never interprets it.
// It marks "body lives on S3", not a format — format is on the row's formatType.
export const STORAGE_REF_TYPE = 'S3_XLSX';

/**
 * THE canonical processors for the storage docId. Any code that receives a
 * `storage.docId` (document rows, history entries) must go through these —
 * never ad-hoc string splitting — so the format has exactly one owner.
 *
 * A null/undefined `versionNumber` composes the bare form, which is how an
 * unversioned bucket's saves are addressed.
 */
export const composeVersionedDocId = (fileId: number, versionNumber?: number | null): string =>
    versionNumber == null ? `${fileId}` : `${fileId}_${versionNumber}`;

export const parseVersionedDocId = (docId: string): { fileId: number; version?: number } => {
    if (typeof docId !== 'string' || !STORAGE_DOC_ID.test(docId)) {
        throw new Error(`Malformed storage docId "${docId}" — expected "<docId>" or "<docId>_<versionNumber>"`);
    }
    const [fileId, version] = docId.split('_').map(Number);
    return version == null || Number.isNaN(version) ? { fileId } : { fileId, version };
};

/** True when this docId names a specific version — i.e. the lineage has history. */
export const docIdHasVersionHistory = (docId?: string | null): boolean => typeof docId === 'string' && docId.includes('_');

/** 8-char hex uid — minted once per document lineage (one S3 key for its whole life). */
const mintUid8 = (): string => Math.random().toString(16).slice(2, 10).padEnd(8, '0');

// ---------------------------------------------------------------------------
// Mnemosyne service calls
// ---------------------------------------------------------------------------

// axios throws on a non-2xx before any caller reads responseMessage, so the
// server's actual reason was being lost behind a generic failure.
const withServerMessage =
    (step: string) =>
    (err: any): never => {
        const serverMessage = err?.response?.data?.responseData?.responseMessage;
        throw serverMessage ? new Error(`${step}: ${serverMessage}`) : err;
    };

interface SaveMetadataInput {
    categoryId: number;
    fileName: string;
    fileSizeKb: number;
    fileType: string;
    flowId: number;
    mnemosyneOrgId?: string;
}

const saveFileMetadata = async (input: SaveMetadataInput, orgId: string): Promise<{ docId: number; fileName?: string }> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/saveMetadata`,
        data: input,
        headers: { 'organization-id': orgId },
    }).catch(withServerMessage('Could not create the file record'));
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK') {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to save file metadata');
    }
    const docId = data?.fileMetadata?.id;
    if (docId == null) {
        throw new Error('saveMetadata response missing fileMetadata.id');
    }
    // The server may clean the fileName it stores — the STORED name is what the
    // S3 key derives from, so it's the one every later presign must reuse.
    return { docId, fileName: data?.fileMetadata?.fileName };
};

/** Presigned PUT URL for the lineage's single S3 key — the EXISTING /file/signedUrl
 *  flow. Key derivation is deterministic from (org, category, fileName), so the same
 *  fileName always presigns the same key every version. */
const fetchPresignedUploadUrl = async (
    input: { fileName: string; fileSizeKb: number; fileType: string; categoryId: number; mnemosyneOrgId?: string },
    orgId: string,
): Promise<string> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/signedUrl`,
        data: input,
        headers: { 'organization-id': orgId },
    }).catch(withServerMessage('Could not get an upload URL'));
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK' || !data?.signedUrl) {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to get presigned URL');
    }
    return data.signedUrl;
};

/**
 * PUT the bytes and report the S3 object version the write created, when there
 * is one.
 *
 * A versioning-enabled bucket answers with `x-amz-version-id`, and that id is
 * the write's identity: mnemosyne records it verbatim, so a register can never
 * attribute another writer's bytes to this save (the RECUR-27628 race).
 *
 * Undefined is a NORMAL outcome, not a failure — it means this bucket keeps no
 * versions (versioning off), or the browser cannot read the header because the
 * bucket's CORS policy does not list it under `ExposeHeaders` (it is not
 * CORS-safelisted). Either way the object was written; the save simply proceeds
 * without history. This is what makes the feature work on every bucket with no
 * infrastructure prerequisite.
 */
const putBytesToS3 = async (signedUrl: string, bytes: ArrayBuffer, mime: string): Promise<string | undefined> => {
    const response = await fetch(signedUrl, {
        method: 'PUT',
        body: bytes,
        headers: new Headers({ 'Content-Type': mime }),
    });
    if (!response.ok) {
        throw new Error(`S3 PUT failed: ${response.status} ${response.statusText}`);
    }
    // Optional-chained: a mocked//minimal Response may carry no headers at all.
    // "null" is what a version-SUSPENDED bucket reports — no more useful than a
    // missing header, so it degrades the same way.
    const versionId = response.headers?.get?.('x-amz-version-id');
    return versionId && versionId !== 'null' ? versionId : undefined;
};

/** The lineage fileName is minted once and never changes — cache it per docId so
 *  subsequent saves don't refetch metadata. */
const docIdFileNameCache = new Map<number, string>();

const resolveFileName = async (docId: number): Promise<string> => {
    const cached = docIdFileNameCache.get(docId);
    if (cached) return cached;
    const res = await axios({ method: 'POST', url: `${mnemosyneBaseUrl}/file/get-file-meta-data?fileId=${docId}` });
    const fileName = res?.data?.docMetaData?.fileName;
    if (!fileName) {
        throw new Error(res?.data?.responseData?.responseMessage ?? 'Failed to resolve file metadata');
    }
    docIdFileNameCache.set(docId, fileName);
    return fileName;
};

/**
 * Register the just-PUT bytes as a version of the doc — the EXISTING JSON
 * `POST /file/v2/saveMetadata` with `id` set (version-register mode). mnemosyne
 * records the versionId in its doc_versions table and answers with the sequential
 * versionNumber on the fileMetadata; raw S3 ids are never persisted on this side.
 *
 * Draft lifecycle mirrors the aica document row: `isDraft: true` (autosave
 * tick) re-points the lineage's single live draft version in place — SAME
 * versionNumber; `isDraft: false` (Save & Close) finalizes it. History grows one
 * version per commit, however many autosave ticks preceded it.
 *
 * `fileSizeKb` is sent because mnemosyne does no S3 round-trip of its own on this
 * call: the caller already knows the size, so a HEAD to rediscover it would be a
 * wasted request on every autosave tick. Sending the SAME sizeKb the
 * create/presign calls sent also keeps mnemosyne's name+size+type checksum
 * consistent across the lineage.
 *
 * `versionId` is OPTIONAL. With one, mnemosyne records a numbered version and
 * answers with its `versionNumber`. Without one (unversioned bucket, or the
 * header not exposed to the browser) it records no version, answers with none,
 * and this returns undefined — the caller then stores a version-less ref and the
 * document simply has no history.
 */
const registerVersion = async (
    docId: number,
    orgId: string,
    isDraft: boolean,
    fileSizeKb: number,
    versionId?: string,
): Promise<number | undefined> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/v2/saveMetadata`,
        data: { id: docId, orgId, isDraft, fileSizeKb, ...(versionId ? { versionId } : {}) },
        headers: { 'organization-id': orgId },
    }).catch(withServerMessage('Version register failed'));
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK') {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to register file version');
    }
    // Absent versionNumber is expected in the unversioned mode; only a mismatch
    // (we sent a versionId but got no number back) is a real failure.
    const versionNumber = data?.fileMetadata?.versionNumber;
    if (versionId && versionNumber == null) {
        throw new Error('Version register did not return a versionNumber for a versioned upload');
    }
    return versionNumber ?? undefined;
};

/**
 * Get a presigned download URL for a stored file via mnemosyne's
 * `downloadFilePresigned` (query params; the server reads them as
 * `@RequestParam`). `version` is the sequential number, which mnemosyne resolves
 * to an S3 object version internally; omitting it (the unversioned mode, where
 * no number exists) resolves to whatever the key currently holds.
 */
const fetchDownloadUrl = async (fileId: number, version?: number): Promise<string> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/downloadFilePresigned?fileId=${fileId}` + (version == null ? '' : `&version=${version}`),
    }).catch(withServerMessage('Could not get a download URL'));
    const data = res?.data;
    if (!data?.signedUrl) {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to get download URL');
    }
    return data.signedUrl;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface UploadBlobVersionArgs {
    bytes: ArrayBuffer;
    mime: string;
    /** Extension (no dot) for the one-time minted fileName. */
    extension: string;
    /** Prefix for the one-time minted fileName, e.g. `sheet`, `report`. */
    fileNamePrefix: string;
    assesseeOrgId: string;
    investorOrgId?: string;
    /** The lineage's existing composed docId, when this is not the first save. */
    existingDocId?: string;
    /** Autosave tick: re-points the live draft version in place. Absent/false = commit. */
    isDraft?: boolean;
    /** Skip the version-register step for a blob that is written once and never
     *  updated: it mints its own doc and S3 key per write, so there is no lineage
     *  to version. Also keeps such writes off `/file/v2/saveMetadata`, whose
     *  register mode is not deployed everywhere yet. */
    skipVersionRegister?: boolean;
}

/**
 * PUT bytes onto a lineage's single S3 key and return the composed docId to
 * persist. Mints a docId (and its one-time filename) only on the first save.
 * A versioning-enabled bucket numbers each save; one without versioning
 * overwrites and the docId stays version-less.
 */
export const uploadBlobVersion = async ({
    bytes,
    mime,
    extension,
    fileNamePrefix,
    assesseeOrgId,
    investorOrgId,
    existingDocId,
    isDraft,
    skipVersionRegister,
}: UploadBlobVersionArgs): Promise<string> => {
    const sizeKb = Math.round(bytes.byteLength / 1000);
    // A malformed existingDocId must THROW: swallowing it would mint a fresh
    // lineage and silently fork the document where an update was expected.
    let docId = existingDocId ? parseVersionedDocId(existingDocId).fileId : undefined;
    let fileName: string;
    if (docId == null) {
        const minted = await saveFileMetadata(
            {
                categoryId: EDITOR_BLOBS_CATEGORY_ID,
                fileName: `${fileNamePrefix}_${mintUid8()}.${extension}`,
                fileSizeKb: sizeKb,
                fileType: mime,
                flowId: EDITOR_BLOBS_FLOW_ID,
                ...(investorOrgId ? { mnemosyneOrgId: investorOrgId } : {}),
            },
            assesseeOrgId,
        );
        docId = minted.docId;
        // The S3 key derives from the name the server stored, which it may have cleaned.
        fileName = minted.fileName ?? (await resolveFileName(docId));
        docIdFileNameCache.set(docId, fileName);
    } else {
        fileName = await resolveFileName(docId);
    }

    const signedUrl = await fetchPresignedUploadUrl(
        {
            fileName,
            fileSizeKb: sizeKb,
            fileType: mime,
            categoryId: EDITOR_BLOBS_CATEGORY_ID,
            ...(investorOrgId ? { mnemosyneOrgId: investorOrgId } : {}),
        },
        assesseeOrgId,
    );
    const s3VersionId = await putBytesToS3(signedUrl, bytes, mime);
    if (skipVersionRegister) {
        return composeVersionedDocId(docId);
    }
    const versionNumber = await registerVersion(docId, assesseeOrgId, isDraft === true, sizeKb, s3VersionId);

    return composeVersionedDocId(docId, versionNumber);
};

/** Presigned download URL. A versioned docId resolves to exactly those bytes. */
export const getBlobDownloadUrl = async (docId: string): Promise<string> => {
    if (!docId) {
        throw new Error('Cannot download a blob without a storage docId');
    }
    const { fileId, version } = parseVersionedDocId(docId);
    return fetchDownloadUrl(fileId, version);
};
